/* ============================================================
   tokenStore.js — the session record, its expiry math, and cross-tab sync.

   A module-level singleton rather than React state, because apiClient must read
   the access token from plain async code that has no hooks available. React
   binds to it through useSyncExternalStore (see auth/AuthProvider.jsx), which
   works because every write replaces `cached` wholesale — the snapshot identity
   only changes when the session actually changes.

   Storage key stays `tf-auth` so anyone already signed in stays signed in.
   ============================================================ */

const KEY = 'tf-auth'

// Renew this far ahead of expiry. Covers clock drift between browser and server
// and, more importantly, means a request never has to eat a guaranteed 401 first.
export const CLOCK_SKEW_MS = 60_000

function readRaw() {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY)) || null
    // Sessions stored before the refresh token moved to an HttpOnly cookie still
    // carry one. Never keep it in memory; it is dropped from storage on next persist.
    if (raw) delete raw.refresh_token
    return raw
  } catch {
    return null
  }
}

let cached = readRaw()
const subscribers = new Set()

function emit() {
  subscribers.forEach((fn) => fn())
}

function persist() {
  try {
    localStorage.setItem(KEY, JSON.stringify(cached))
  } catch {
    /* quota or private mode — the in-memory session still works for this tab */
  }
}

export function getSession() {
  return cached
}
export function getAccessToken() {
  return cached?.access_token ?? null
}

export function subscribe(fn) {
  subscribers.add(fn)
  return () => subscribers.delete(fn)
}

/** Re-read from storage. Another tab may have rotated the pair since we last looked. */
export function reloadFromStorage() {
  cached = readRaw()
  return cached
}

export function isExpiring(skew = CLOCK_SKEW_MS) {
  if (!cached?.access_token) return false
  // Sessions written before this field existed can't be reasoned about; let the
  // reactive 401 path handle those rather than refreshing on every request.
  if (!cached.expires_at) return false
  return Date.now() >= cached.expires_at - skew
}

/**
 * Store a login/signup/refresh response.
 *
 * No refresh token here: the server keeps it in an HttpOnly cookie that JS can
 * neither read nor leak (backend/users/plugins.py set_refresh_cookie).
 *
 * The `data` MERGE is load-bearing. /users/login and /users/refresh return a
 * user record WITHOUT `permissions` — only GET /users/me resolves those
 * (backend/users/serializers.py: serialize_user's with_permissions defaults to
 * False). A naive assignment would therefore wipe the permission list on the
 * first background refresh, and every permission-gated nav item would vanish
 * mid-session.
 */
export function setSession(res) {
  cached = {
    access_token: res.access_token,
    expires_in: res.expires_in,
    expires_at: Date.now() + (res.expires_in ?? 1800) * 1000,
    data: { ...(cached?.data ?? {}), ...(res.data ?? {}) },
  }
  persist()
  emit()
  return cached
}

/** Merge the GET /users/me payload — this is what puts `permissions` on the session. */
export function mergeUser(user) {
  if (!cached || !user) return cached
  cached = { ...cached, data: { ...cached.data, ...user } }
  persist()
  emit()
  return cached
}

export function clearSession() {
  cached = null
  setPreviewRoleId(null)
  setPreviewUserId(null)
  try {
    localStorage.removeItem(KEY)
  } catch {
    /* ignore */
  }
  emit()
}

/* "View as role" (profile menu → Roles). Per TAB, in sessionStorage: a system
   administrator can keep their own view in one tab and a role in another.
   apiClient sends it as X-Preview-Role; the server swaps in that role's name
   and tags (backend/users/permissions.py apply_role_preview). */
const PREVIEW_KEY = 'tf-preview-role'

export function getPreviewRoleId() {
  try {
    const raw = sessionStorage.getItem(PREVIEW_KEY)
    return raw ? Number(raw) : null
  } catch {
    return null
  }
}

export function setPreviewRoleId(roleId) {
  try {
    if (roleId == null) sessionStorage.removeItem(PREVIEW_KEY)
    else sessionStorage.setItem(PREVIEW_KEY, String(roleId))
  } catch {
    /* private mode — preview simply does not stick */
  }
}

/* "View as user" (Activity analytics → View this Role). Same per-tab storage.
   apiClient sends it as X-Preview-User; the server swaps in that user's id,
   role and tags, read-only (backend/users/permissions.py apply_user_preview). */
const PREVIEW_USER_KEY = 'tf-preview-user'

export function getPreviewUserId() {
  try {
    return sessionStorage.getItem(PREVIEW_USER_KEY) || null
  } catch {
    return null
  }
}

export function setPreviewUserId(userId) {
  try {
    if (userId == null) sessionStorage.removeItem(PREVIEW_USER_KEY)
    else sessionStorage.setItem(PREVIEW_USER_KEY, String(userId))
  } catch {
    /* private mode — preview simply does not stick */
  }
}

// Cross-tab: sign-out and token rotation both propagate. `e.key === null` is a
// storage.clear() from another tab.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key !== KEY && e.key !== null) return
    cached = readRaw()
    emit()
  })
}
